import { action } from '@reatom/framework'

import { Result } from '@blade/std-lib'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { apiRequest } from '@root/shared/api'

import type { WalletId } from '@root/entities/wallet'

/**
 * A trade made on our side, from a wallet whose key we keep.
 *
 * Everything else in this feature signs on the machine it runs on, through a
 * store only the desktop build has — which is why the web's buy button has
 * never bought anything and offered a download instead. A wallet made on the
 * server has its key in the signer, so the router builds the transaction, the
 * signer signs it, and what comes back is a signature.
 *
 * Only for a wallet whose custody is `server`. One held on a device has to be
 * signed on that device, and the server refuses rather than pretending.
 */

export type ServerSwapSide = 'buy' | 'sell'

export type ServerSwapProblem =
  | 'no-route'
  | 'router-unavailable'
  | 'held-on-a-device'
  | 'unknown'

export const serverSwapText: Record<ServerSwapProblem, string> = {
  'no-route':
    'No way to make this trade was found. The amount may be more than the pool can take.',
  'router-unavailable':
    'Trading is not available right now. Nothing was spent.',
  'held-on-a-device':
    'This wallet was added from the desktop app, so it can only be traded from there.',
  unknown: 'That trade did not go through. Nothing was spent.',
}

const problemOf = (error: {
  _tag: string
  code?: string
}): ServerSwapProblem => {
  if (error.code === 'NO_ROUTE') {
    return 'no-route'
  }
  if (error.code === 'ROUTER_UNAVAILABLE') {
    return 'router-unavailable'
  }
  if (error.code === 'DEVICE_WALLET') {
    return 'held-on-a-device'
  }
  return 'unknown'
}

export const swapFromServerWalletAction = action(
  async (
    ctx,
    input: {
      walletId: WalletId
      side: ServerSwapSide
      mint: string
      amount: string
      slippagePercent?: number
      priorityFeeMicroLamports?: number
    },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.wallet.walletSwapCreate, [input]),
    )

    return result.pipe(Result.mapError(problemOf))
  },
  'swapFromServerWalletAction',
)
